"use client";

import { useEffect } from "react";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import "./shipping.css";


export default function ShippingInfoPage() {
  useEffect(() => {
    const loadScript = (src) =>
      new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = src;
        s.async = false; // ensure load order
        s.onload = resolve;
        s.onerror = reject;
        document.body.appendChild(s);
      });

    let ro;

    (async () => {
      try {
        console.log("Loading map scripts...");
        await loadScript("/js/mapdata.js");
        await loadScript("/js/usmap.js");

        console.log("Map scripts loaded.");
        const mapContainer = document.getElementById("map");

        // Ensure visible height before render
        if (mapContainer) {
          const rect = mapContainer.getBoundingClientRect();
          if (rect.height < 200) {
            mapContainer.style.minHeight = "500px";
          }
        }

        // Wait a tick, then trigger resize or redraw
        setTimeout(() => {
          if (window.simplemaps_usmap?.resize) {
            console.log("Calling simplemaps_usmap.resize()");
            window.simplemaps_usmap.resize();
          } else {
            console.log("Dispatching window resize event");
            window.dispatchEvent(new Event("resize"));
          }
        }, 250);

        // Watch container width and auto-resize
        const target = mapContainer?.parentElement || mapContainer;
        if (target && "ResizeObserver" in window) {
          ro = new ResizeObserver(() => {
            try {
              window.simplemaps_usmap?.resize?.();
            } catch { }
          });
          ro.observe(target);
        }
      } catch (err) {
        console.error("Error loading map scripts:", err);
      }
    })();

    return () => {
      ro?.disconnect?.();
    };
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-white text-gray-800">

      <section
        className="relative w-full bg-green-50 border-y border-gray-300"
        style={{
          backgroundImage: "url('/images/bg-pattern-1.png')",
          backgroundRepeat: "repeat",
        }}
      >
        <div className="container mx-auto px-4 py-12 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-[#1f3d1f] mb-4">
            Shipping Information
          </h1>
          <p className="text-gray-700 text-base md:text-lg max-w-3xl mx-auto">
            CareFirst Specialty Pharmacy is proud to ship to all 50 U.S. states.<br />
            View our service area on the interactive map below.
          </p>
        </div>
      </section>

      <main className="flex-1 container mx-auto px-4 py-10">
        {/* Responsive Map Container */}
        <div className="map-wrapper mb-10">
          <div id="map" className="w-full"></div>
        </div>


        {/* Shipping Information */}
        <div className="text-gray-700 leading-relaxed">
          <h2 className="text-2xl font-semibold text-[#1f3d1f] mb-4">
            Shipping Information
          </h2>
          <p className="mb-4">
            Most orders will ship within 4 to 5 days of order placement (we do
            not ship Saturdays, Sundays, or holidays). However, some compounds
            may take additional time to process. Processing time may vary for
            each item on an order due to ingredient availability and quality
            testing for finished preparations prior to shipment.
          </p>

          <p className="mb-4">
            Your order will be shipped via Standard Shipping unless you specify
            an alternative at an additional charge. We offer Standard Shipping,
            Priority, and Overnight shipping. All promotional shipping offers
            will be shipped as Standard Shipping.
          </p>

          <p className="mb-4">
            We ship non-prescription items to all US states and territories.
            Prescription medications can only be shipped to states in which we
            are licensed. To find out if we ship to your state{" "}
            <a
              href="#licensed-states"
              className="text-green-700 font-semibold hover:underline"
            >
              click here
            </a>
            .
          </p>

          <p className="mb-6">
            Please contact us for the most up to date information. Orders
            shipped to PO boxes, APO/FPO, Alaska, Hawaii and US territories
            cannot be expedited.
          </p>

          {/* Shipping Table */}
          <div className="overflow-x-auto mb-8">
            <table className="w-full border border-gray-300 text-sm md:text-base text-left">
              <thead className="bg-green-100 text-[#1f3d1f] font-semibold">
                <tr>
                  <th className="p-3 border-b border-gray-300">
                    Shipping Method
                  </th>
                  <th className="p-3 border-b border-gray-300">
                    Estimated Delivery Time
                  </th>
                  <th className="p-3 border-b border-gray-300">Price</th>
                </tr>
              </thead>
              <tbody>
                {[
                  [
                    "Standard Shipping",
                    "3-5 business days (depending on your location)",
                    "$4.95",
                  ],
                  [
                    "Priority Shipping (via USPS)",
                    "1-3 business days (depending on your location)",
                    "$8.95",
                  ],
                  [
                    "CFS Upgraded Shipping (Excluding Alaska and Hawaii)",
                    "1-3 business days (Depending on location, via UPS or FedEx)",
                    "$14.95",
                  ],
                  [
                    "2nd Day Air / Ground Ice (Excluding Alaska and Hawaii)",
                    "1-2 business days (excluding weekends)",
                    "$19.95",
                  ],
                  [
                    "Next Day Air (Excluding Alaska and Hawaii)",
                    "1-2 business days (excluding weekends)",
                    "$29.95",
                  ],
                  [
                    "Next Day Air Ice (Excluding Alaska and Hawaii)",
                    "1-2 business days (excluding weekends)",
                    "$39.95",
                  ],
                  [
                    "Alaska/Hawaii 2nd Day Air",
                    "1-2 business days (excluding weekends)",
                    "$60",
                  ],
                  [
                    "Alaska/Hawaii Overnight",
                    "1-2 business days (excluding weekends)",
                    "$100",
                  ],
                  [
                    "Alaska/Hawaii ICE Overnight",
                    "1-2 business days (excluding weekends)",
                    "$120",
                  ],
                ].map(([method, time, price], i) => (
                  <tr key={i} className="odd:bg-white even:bg-gray-50">
                    <td className="p-3 border-b border-gray-200">{method}</td>
                    <td className="p-3 border-b border-gray-200">{time}</td>
                    <td className="p-3 border-b border-gray-200">{price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mb-6">
            Control medications will require an Adult Signature for delivery and
            incur an additional charge of <strong>$2.95</strong>.
          </p>

          <p className="mb-6">
            If Standard Shipping is selected, your order may ship via a variety
            of carriers. Some UPS orders may be delivered by the Postal Service.
            Your Shipping Confirmation email will tell you which carrier is
            handling your package and we will provide you with a tracking link.
          </p>

          <h3 className="text-xl font-semibold text-[#1f3d1f] mt-8 mb-2">
            Shipping Status
          </h3>
          <p className="mb-6">
            You will receive an e-mail informing you that the order has shipped.
            The email will include a tracking number.
          </p>

          <h3
            id="licensed-states"
            className="text-xl font-semibold text-[#1f3d1f] mb-3"
          >
            We Currently Ship Prescriptions To:
          </h3>
          <p className="text-sm md:text-base text-gray-700">
            Alabama (AL), Alaska (AK), Arizona (AZ), Arkansas (AR), California
            (CA), Colorado (CO), Connecticut (CT), Delaware (DE), Washington
            D.C. (DC), Florida (FL), Georgia (GA), Hawaii (HI), Idaho (ID),
            Illinois (IL), Indiana (IN), Iowa (IA), Kansas (KS), Kentucky (KY),
            Louisiana (LA), Maine (ME), Maryland (MD), Massachusetts (MA),
            Michigan (MI), Minnesota (MN), Mississippi (MS), Missouri (MO),
            Montana (MT), Nebraska (NE), Nevada (NV), New Hampshire (NH), New
            Jersey (NJ), New Mexico (NM), New York (NY), North Carolina (NC),
            North Dakota (ND), Ohio (OH), Oklahoma (OK), Oregon (OR),
            Pennsylvania (PA), Rhode Island (RI), South Carolina (SC), South
            Dakota (SD), Tennessee (TN), Texas (TX), Utah (UT), US Virgin
            Islands (VI), Vermont (VT), Virginia (VA), Washington (WA),
            Wisconsin (WI), West Virginia (WV), Wyoming (WY)
          </p>
        </div>
      </main>
    </div>
  );
}
